import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';

import {
  CustomAlert,
  Error403Unauthorized,
  MultipleSelect,
  Phone,
} from '@app/components';
import { Avatar } from '@app/components/Avatar';
import { FormItemField, SnilsInput } from '@app/components/FormFields';
import { ClientDuplicateModal } from '@app/components/Modals/ClientDuplicateModal';
import { phoneValidateHandler } from '@app/components/Phone/utils';
import { DictionarySelect } from '@app/components/RecordModal/components/MedicalCard/components';
import { MedicalDictionaryType } from '@app/components/RecordModal/components/MedicalCard/data';
import {
  DATE_PICKER_FORMAT,
  EMAIL_LENGTH_VALIDATION,
  EMAIL_TYPE_VALIDATION,
  ERROR,
  ERROR_REQUIRED_FIELD,
  GENDER,
  ISO_FORMAT_INDEX,
  OFF,
  ONE_TIME_CODE,
  PREFIX,
} from '@app/constants';
import { rangePickerLocale } from '@app/constants/rangePickerLocale';
import { useClientsAccessRights } from '@app/hooks';
import type { NTags } from '@app/types';
import { getContrastYIQ } from '@app/utils';

import {
  useAddClientEffects,
  useAddClient,
} from '@pages/Clients/AddClient/hooks';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import '@pages/Clients/AddClient/styles/AddClient.desktop.scss';
import '@pages/Clients/AddClient/styles/AddClient.tablet.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { Item } = Form;

export const AddClient = () => {
  const {
    form,
    data,
    clientId,
    isEditMode,
    setData,
    sendDataToDb,
    items,
    photo,
    serverError,
    setServerError,
    setVerifiedPhone,
    initialLoadData,
    isSaving,
    handleCancel,
    languages,
    tags,
    selectedTags,
    setSelectedTags,
    fetchData,
    initSelectedTags,
    isLoader,
    setPhonesValidation,
    activeBranchCountry,
    phonesValidation,
    IsTagsChange,
    duplicateCheckResult,
    closeDuplicateModalHandler,
    openExistingClientHandler,
    mergeClientsHandler,
  } = useAddClient();

  useAddClientEffects({ fetchData, initSelectedTags });

  const { hasUserRightToCreate, hasUserRightToEdit } = useClientsAccessRights();

  if (
    (!isEditMode && !hasUserRightToCreate) ||
    (isEditMode && !hasUserRightToEdit)
  ) {
    return <Error403Unauthorized />;
  }

  if (!data && clientId) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}add-client`}>
      <Form
        className="bc-form"
        form={form}
        name="basic"
        onFinish={sendDataToDb}
        autoComplete={OFF}
        initialValues={{
          Phone1: {
            CountryId: activeBranchCountry || 1,
            Phone: '',
          },
          Phone2: {
            CountryId: activeBranchCountry || 1,
            Phone: '',
          },
        }}
        scrollToFirstError={true}
        onFinishFailed={() => {
          phoneValidateHandler(form, phonesValidation.phone1, 'Phone1');
        }}
      >
        {Boolean(clientId) && (
          <div className="avatar-container-tablet">
            <Avatar
              src={photo || null}
              nameFirst={initialLoadData.NameFirst}
              nameLast={initialLoadData.NameLast}
            />
            <Dropdown menu={{ items }}>
              <Button type="link">Изменить</Button>
            </Dropdown>
          </div>
        )}
        <Space
          direction="vertical"
          size="middle"
          className="form-space form-fields-space-container"
        >
          <div className="field-container">
            <Text data-testid="surname" strong>
              Фамилия
            </Text>
            <Form.Item name="NameLast" className="mg-0" initialValue="">
              <Input
                autoComplete={ONE_TIME_CODE}
                data-testid="surname-input"
                size="large"
                value={data?.NameLast || ''}
                maxLength={60}
                onChange={(event) => {
                  setData({
                    ...data,
                    NameLast: event.target?.value || '',
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="name" className="label-require" strong>
              Имя
            </Text>
            <Form.Item
              name="NameFirst"
              rules={[
                { required: true, message: ERROR_REQUIRED_FIELD },
                {
                  validator: async (_, value) => {
                    if (!value.trim()) {
                      return Promise.reject(
                        new Error(
                          'Поле не должно содержать только пробельные символы'
                        )
                      );
                    }
                  },
                },
              ]}
              className="mg-0"
            >
              <Input
                maxLength={60}
                autoComplete={ONE_TIME_CODE}
                data-testid="name-input"
                id="test-client-addition-name-field"
                size="large"
                value={data?.NameFirst || ''}
                onChange={({ target: { value: NameFirst = '' } }) =>
                  setData({ ...data, NameFirst })
                }
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="patronymic" strong>
              Отчество
            </Text>
            <Form.Item name="NameMiddle" className="mg-0">
              <Input
                maxLength={80}
                data-testid="patronymic-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                value={data?.NameMiddle || ''}
                onChange={({ target: { value: NameMiddle = '' } }) =>
                  setData({ ...data, NameMiddle })
                }
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="email" strong>
              Email
            </Text>
            <Form.Item
              name="Email"
              className="mg-0"
              rules={[EMAIL_TYPE_VALIDATION, EMAIL_LENGTH_VALIDATION]}
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                data-testid="email-input"
                size="large"
                value={data?.Email || ''}
                onChange={({ target: { value: Email = '' } }) =>
                  setData({ ...data, Email })
                }
                maxLength={100}
              />
            </Form.Item>
          </div>
          <div className="field-container phone-container">
            <Text
              data-testid="main-phone"
              className="label-require phone-label"
              strong
            >
              Основной телефон
            </Text>
            <Item name="Phone1" style={{ width: '100%' }} className="mg-0">
              <Phone
                name="Phone1"
                required
                disabled={isLoader}
                onValidate={(validation) =>
                  setPhonesValidation((prev) => ({
                    ...prev,
                    phone1: validation,
                  }))
                }
              />
            </Item>
          </div>

          <SnilsInput
            fieldName="Snils"
            label="СНИЛС пациента (11 цифр)"
            disabled={isLoader}
          />

          <DictionarySelect
            disabled={isLoader}
            type={MedicalDictionaryType.IdentityDocuments}
            title="Удостоверение личности"
            name={'IdentityDocumentType'}
            placeholder=""
            isStatic
          />

          <FormItemField
            label="Номер документа"
            fieldName="IdentityDocumentNumber"
          >
            <Input size="large" maxLength={100} disabled={isLoader} />
          </FormItemField>

          <FormItemField
            label="Адрес регистрации"
            fieldName="RegistrationAddress"
          >
            <Input size="large" maxLength={100} disabled={isLoader} />
          </FormItemField>

          <div className="field-container phone-verify-wrapper">
            <div className="first-column"></div>
            <Form.Item
              name="IsPhoneVerified"
              className="mg-0"
              valuePropName="checked"
            >
              <Checkbox
                data-testid="phone-confirmed"
                onChange={({ target: { checked: IsPhoneVerified } }) => {
                  setData({ ...data, IsPhoneVerified });
                  if (IsPhoneVerified) {
                    setVerifiedPhone(data.Phone1);
                  }
                }}
              >
                <Text type="secondary">Телефон подтверждён</Text>
              </Checkbox>
            </Form.Item>
          </div>
          <div className="field-container phone-container">
            <Text data-testid="main-phone" className="label phone-label" strong>
              Дополнительный телефон
            </Text>
            <Item name="Phone2" style={{ width: '100%' }} className="mg-0">
              <Phone
                name="Phone2"
                required
                disabled={isLoader}
                onValidate={(validation) =>
                  setPhonesValidation((prev) => ({
                    ...prev,
                    phone2: validation,
                  }))
                }
              />
            </Item>
          </div>

          <div className="field-container">
            <Text data-testid="gender" strong>
              Пол
            </Text>
            <Form.Item name="Gender" className="mg-0">
              <Select
                data-testid="gender-select"
                className="field-selector"
                size="large"
                options={GENDER}
                onChange={(value) => {
                  setData({
                    ...data,
                    Gender: value,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="language" strong>
              Язык для общения
            </Text>
            <Form.Item name="LanguageId" className="mg-0">
              <Select
                data-testid="language-select"
                className="field-selector"
                size="large"
                options={languages.map((language) => {
                  return {
                    label: language.Name,
                    value: language.Id,
                  };
                })}
                onChange={(value) => {
                  setData({
                    ...data,
                    LanguageId: value,
                  });
                }}
                allowClear={true}
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="tag" strong>
              Тег
            </Text>
            <Form.Item name="Tags" className="mg-0" initialValue={selectedTags}>
              <MultipleSelect
                dataTestId="tag-select"
                size="large"
                className="tags-selector"
                placeholder=""
                allowClear={true}
                showSearch
                filterName="name"
                disabled={!IsTagsChange}
                allOptions={tags.map((tag: NTags.Tag) => ({
                  label: (
                    <Tag
                      key={tag?.IconId}
                      icon={
                        <FontAwesomeIcon
                          className="tag-icon"
                          icon={tag?.IconId as IconProp}
                          style={{
                            color: getContrastYIQ(tag.Color),
                          }}
                        />
                      }
                      color={tag.Color || 'white'}
                    >
                      <Text
                        data-testid="name-selected-tag"
                        style={{ color: getContrastYIQ(tag.Color) }}
                      >
                        {tag.Name}
                      </Text>
                    </Tag>
                  ),
                  value: tag.Id,
                  name: tag.Name,
                }))}
                width={'100%'}
                showActions={false}
                defaultSelected={Boolean(clientId)}
                selectedOption={selectedTags}
                setSelectedOptions={setSelectedTags}
              />
            </Form.Item>
          </div>
          <div className="field-container">
            <Text data-testid="birth-date" strong>
              Дата рождения
            </Text>
            <Form.Item name="DateBirth" className="mg-0">
              <DatePicker
                data-testid="birth-date-select"
                autoComplete={ONE_TIME_CODE}
                locale={rangePickerLocale}
                className="field-selector field-date"
                classNames={{ popup: { root: 'record-modal-calendar' } }}
                size="large"
                format={DATE_PICKER_FORMAT}
                onChange={(date) =>
                  setData({
                    ...data,
                    DateBirth: date
                      ? date.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                      : null,
                  })
                }
                disabledDate={(current) =>
                  current.unix() >= moment().startOf('day').unix()
                }
              />
            </Form.Item>
          </div>
          <div className="field-container textarea-container">
            <Text data-testid="birth-date" strong>
              Примечание
            </Text>
            <Form.Item className="mg-0" name="Comment">
              <TextArea rows={4} maxLength={600} />
            </Form.Item>
          </div>
          {serverError && (
            <CustomAlert
              errorType={ERROR}
              serverError={serverError}
              setServerError={setServerError}
            />
          )}
          <div className="save-button-wrapper">
            <Button
              data-testid="save"
              id="test-add-client-sending-button"
              type="primary"
              htmlType="submit"
              className="save-button"
              loading={isSaving}
              size="large"
            >
              Сохранить
            </Button>

            <Button
              data-testid="cancel"
              className="save-button"
              disabled={isSaving}
              size="large"
              onClick={handleCancel}
            >
              Отменить
            </Button>
          </div>
        </Space>
      </Form>

      {clientId ? (
        <div className="avatar-container-desktop">
          <Avatar
            className={`avatar${!photo ? ' avatar-without-photo' : ''}`}
            src={photo || null}
            nameFirst={initialLoadData.NameFirst}
            nameLast={initialLoadData.NameLast}
          />
          <Dropdown menu={{ items }}>
            <Button data-testid="change-avatar" type="link">
              Изменить
            </Button>
          </Dropdown>
        </div>
      ) : (
        ''
      )}
      {duplicateCheckResult && (
        <ClientDuplicateModal
          open={true}
          phone={duplicateCheckResult.CheckClientPhone ?? ''}
          isMergePossible={duplicateCheckResult.IsMergePossible}
          onCancel={closeDuplicateModalHandler}
          onOpenClient={openExistingClientHandler}
          onMerge={mergeClientsHandler}
        />
      )}
    </div>
  );
};
